import { FC, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface EmailModalProps {
  isOpen: boolean;
  memberId?: string;
  memberEmail?: string;
  memberName?: string;
  onClose: () => void;
}

const EmailModal: FC<EmailModalProps> = ({ isOpen, onClose, memberEmail = '', memberName = 'Member' }) => {
  const [selectedTemplate, setSelectedTemplate] = useState('custom');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  // Email templates with pre-built messages
  const templates = [
    { 
      id: 'welcome', 
      name: 'Welcome Email', 
      subject: 'Welcome to Labor Credit Union!',
      message: `Dear ${memberName},

Welcome to Labor Credit Union! We're excited to have you as part of our community.

Our team is here to support your financial goals and help you achieve financial wellness. If you have any questions or need assistance, please don't hesitate to reach out.

Sincerely,
Sarah Johnson`
    },
    { 
      id: 'followup', 
      name: 'Follow-up', 
      subject: 'Following up on your recent activity',
      message: `Dear ${memberName},

I wanted to follow up with you regarding your recent activity. We're here to help answer any questions you may have and ensure you're getting the most out of your membership.

Please feel free to reach out if there's anything we can assist you with.

Sincerely,
Sarah Johnson`
    },
    { 
      id: 'review', 
      name: 'Review Request', 
      subject: 'Quick review of your account',
      message: `Dear ${memberName},

I hope this message finds you well. I wanted to reach out to schedule a quick review of your account to ensure everything is on track with your financial goals.

Please let me know a convenient time for you, and we'll set up a brief call.

Sincerely,
Sarah Johnson`
    },
    { 
      id: 'custom', 
      name: 'Custom Email', 
      subject: '',
      message: `

Sincerely,
Sarah Johnson`
    },
  ];

  // Update subject and message when template changes or member name changes
  useEffect(() => {
    const selected = templates.find(t => t.id === selectedTemplate);
    if (selected) {
      setSubject(selected.subject);
      setMessage(selected.message);
    }
  }, [selectedTemplate, memberName, templates]);

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      {/* Overlay with blur effect */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-20 backdrop-blur-sm transition-opacity" 
        onClick={onClose} 
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div 
          className="relative rounded-xl max-w-2xl w-full transform transition-all"
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(31, 38, 135, 0.1)'
          }}
        >
          {/* Header with gradient */}
          <div 
            className="px-6 py-4 rounded-t-xl"
            style={{
              background: 'linear-gradient(135deg, #f6f8fc, #e6eaf0)',
              borderBottom: '1px solid rgba(203, 213, 225, 0.5)'
            }}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-slate-800">Send Email</h3>
              <button 
                onClick={onClose} 
                className="text-slate-400 hover:text-slate-600 transition-colors rounded-full p-1 hover:bg-slate-100"
              >
                <span className="sr-only">Close</span>
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-5">
            <div className="space-y-5">
              {/* Recipient Email Display */}
              {memberEmail && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    To:
                  </label>
                  <div className="text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded-md border border-slate-200">
                    {memberEmail}
                  </div>
                </div>
              )}

              {/* Template Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email Template
                </label>
                <select
                  value={selectedTemplate}
                  onChange={e => setSelectedTemplate(e.target.value)}
                  className="w-full rounded-md border-slate-200 text-sm bg-white/80 focus:ring-brand-blue focus:border-brand-blue"
                  style={{ boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)' }}
                >
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Subject Line */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Subject</label>
                <input
                  type="text"
                  className="w-full rounded-md border-slate-200 text-sm bg-white/80 focus:ring-brand-blue focus:border-brand-blue"
                  placeholder="Enter email subject..."
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  style={{ boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)' }}
                />
              </div>

              {/* Email Content */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Message</label>
                <textarea
                  rows={10}
                  className="w-full rounded-md border-slate-200 text-sm bg-white/80 focus:ring-brand-blue focus:border-brand-blue"
                  placeholder="Enter your message..."
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  style={{ boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)' }}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div 
            className="px-6 py-4 rounded-b-xl"
            style={{
              background: 'linear-gradient(135deg, #f6f8fc, #e6eaf0)',
              borderTop: '1px solid rgba(203, 213, 225, 0.5)'
            }}
          >
            <div className="flex justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button 
                className="px-4 py-2 text-sm font-medium text-white rounded transition-all"
                style={{
                  background: 'linear-gradient(to bottom, #3b82f6, #2563eb)',
                  boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
                }}
              >
                Send Email
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default EmailModal;
